import { useState } from "react";
import { AlertTriangle, Check, Eye, EyeOff, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useResetUserPassword } from "../hooks/useUsers";

const BORDER = "hsl(210,30%,85%)";

type Props = {
  open: boolean;
  onClose: () => void;
  targetUserId: string;
  targetName: string;
  onSuccess?: () => void;
};

export function ResetPasswordDialog({
  open,
  onClose,
  targetUserId,
  targetName,
  onSuccess,
}: Props) {
  const reset = useResetUserPassword(targetUserId);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [mustChange, setMustChange] = useState(true);
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  function reset_local() {
    setPw("");
    setConfirm("");
    setMustChange(true);
    setShow(false);
    setError(null);
  }

  async function submit() {
    if (pw.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }
    if (pw !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setError(null);
    try {
      await reset.mutateAsync({ newPassword: pw, mustChangePassword: mustChange });
      reset_local();
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset password");
    }
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
      <div
        className="w-[460px] rounded-xl border bg-white p-6 shadow-xl"
        style={{ borderColor: BORDER }}
      >
        <h3 className="text-[15px] font-semibold text-foreground">
          Reset Password for {targetName}
        </h3>
        <p className="mt-1 text-[12px] text-muted-foreground">
          This sets a new password immediately and signs the user into a forced
          change flow on their next login.
        </p>
        <form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="mt-4 space-y-3"
        >
          {/* Honeypots — same pattern as Vault/Profile to absorb autofill. */}
          <input
            type="text"
            name="username"
            autoComplete="username"
            tabIndex={-1}
            aria-hidden="true"
            style={{
              position: "absolute",
              opacity: 0,
              height: 0,
              width: 0,
              pointerEvents: "none",
            }}
          />
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            tabIndex={-1}
            aria-hidden="true"
            style={{
              position: "absolute",
              opacity: 0,
              height: 0,
              width: 0,
              pointerEvents: "none",
            }}
          />
          <div className="space-y-2">
            <Label htmlFor="reset-new">
              New Password <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <Input
                id="reset-new"
                type={show ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                autoComplete="new-password"
                data-lpignore="true"
                data-form-type="other"
                data-1p-ignore="true"
                required
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Toggle visibility"
              >
                {show ? (
                  <EyeOff className="h-4 w-4" strokeWidth={1.5} />
                ) : (
                  <Eye className="h-4 w-4" strokeWidth={1.5} />
                )}
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              At least 8 characters.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="reset-confirm">
              Confirm New Password <span className="text-destructive">*</span>
            </Label>
            <Input
              id="reset-confirm"
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              data-lpignore="true"
              data-form-type="other"
              data-1p-ignore="true"
              required
            />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={mustChange}
              onChange={(e) => setMustChange(e.target.checked)}
              className="mt-0.5 h-4 w-4"
            />
            <span>
              Require user to change password on next login{" "}
              <span className="text-muted-foreground">(recommended)</span>
            </span>
          </label>
          <div
            className="flex items-start gap-2 rounded-md border px-3 py-2 text-[12px]"
            style={{
              borderColor: "hsl(38,92%,75%)",
              background: "hsl(38,92%,96%)",
              color: "hsl(38,92%,25%)",
            }}
          >
            <AlertTriangle
              className="mt-0.5 h-3.5 w-3.5 flex-shrink-0"
              strokeWidth={1.8}
            />
            <span>
              Communicate this password via a secure channel (WhatsApp, in
              person, signed message). Do not email it.
            </span>
          </div>
          {error && (
            <div
              className="rounded-md border px-3 py-2 text-sm"
              style={{
                borderColor: "hsl(0,70%,80%)",
                background: "hsl(0,70%,96%)",
                color: "hsl(0,70%,35%)",
              }}
            >
              {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset_local();
                onClose();
              }}
              disabled={reset.isPending}
            >
              <X className="mr-1 h-4 w-4" strokeWidth={1.5} />
              Cancel
            </Button>
            <Button type="submit" disabled={reset.isPending}>
              {reset.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <Check className="mr-1 h-4 w-4" strokeWidth={1.8} />
              )}
              Reset Password
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
